/**
 * AGENT VIEW
 *
 * Main agent dashboard showing all AI agent runs, chat sessions, and terminal tabs.
 * Three-panel layout: agent list (left), conversation (center), evidence (right).
 */

import { useEffect, useState, useMemo, useCallback, useRef, lazy, Suspense } from "react";
import { useReviewStore, type Run } from "@/stores/reviewStore";
import { useAiChatStore } from "@/stores/aiChatStore";
import { useChatTabStore, type ChatTab } from "@/stores/chatTabStore";
import { useWorldStore } from "@/stores/worldStore";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Play,
  Eye,
  Shield,
  Loader2,
  Plus,
  FolderOpen,
  Search,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Wrench,
  MessageSquare,
  Terminal,
  Bot,
  ExternalLink,
  Trash2,
  Pause,
} from "lucide-react";
import { EvidencePanel } from "./EvidencePanel";
import { NeedsAttentionSection } from "./NeedsAttentionSection";
import { RunErrorCard } from "./RunErrorCard";
import { QuickFix } from "./QuickFix";
import { IntakeTemplates } from "./IntakeTemplates";
import { AIConversation, AIConversationEmpty } from "./AIConversation";
import { ChatConversation } from "@/features/factory/ai-chat/ChatConversation";
import { ChatInput } from "@/features/factory/ai-chat/ChatInput";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useWorkspaceFeatures } from "@/hooks/useWorkspaceFeatures";
import { FirstUseHint } from "@/components/onboarding/FirstUseHint";
import { useUnifiedAgents } from "@/hooks/useUnifiedAgents";
import { useLocalTerminalStore } from "@/stores/localTerminalStore";
import { ACTION_HANDLERS } from "@/lib/data/agent-actions";
import type { UnifiedAgentRow, AgentSource } from "@/lib/data/unified-agents";
import { collectNeedsAttention } from "@/lib/data/unified-agents";
import { STATUS_CONFIG, isActive, statusIcon, type UnifiedAgentStatus } from "@/lib/utils/agent-status";
import { PROVIDER_META, type AiProvider, type AiChatSession } from "@/types/ai-chat";
import type { ChatAttachment } from "@/types/content-blocks";

// Lazy-load LocalTerminalView so Tauri-only code is not bundled in web builds.
const LocalTerminalView = lazy(() =>
  import("@/features/factory/terminal/LocalTerminalView").then((m) => ({
    default: m.LocalTerminalView,
  }))
);

const SOURCE_ICONS: Record<AgentSource, typeof MessageSquare> = {
  chat: MessageSquare,
  terminal: Terminal,
  workflow: Bot,
};

const SOURCE_LABELS: Record<AgentSource, string> = {
  chat: "Chat",
  terminal: "Terminal",
  workflow: "Workflow",
};

const MIN_PANEL_WIDTH = 200;
const MAX_LEFT_PANEL_WIDTH = 400;
const MAX_RIGHT_PANEL_WIDTH = 500;
const DEFAULT_LEFT_WIDTH = 280;
const DEFAULT_RIGHT_WIDTH = 360;

function workflowIssueTypeToTaskIssueType(issueType?: string): "bug" | "wp" | "feature" | "hotfix" {
  if (issueType === "bug") return "bug";
  if (issueType === "hotfix") return "hotfix";
  if (issueType === "wp" || issueType === "refactor") return "wp";
  return "feature";
}

export function AgentView() {
  const {
    runs,
    selectedRunId,
    isLoading,
    fetchRuns,
    selectRun,
    createChatRun,
    hydrateRunActivityFromWorkflow,
  } = useReviewStore();
  const worldProjects = useWorldStore((s) => s.projects);
  const addTask = useWorldStore((s) => s.addTask);
  const { isCloudWorkspace } = useWorkspaceFeatures();

  const sessions = useAiChatStore((s) => s.sessions);
  const sendMessage = useAiChatStore((s) => s.sendMessage);
  const stopSession = useAiChatStore((s) => s.stopSession);
  const createSession = useAiChatStore((s) => s.createSession);
  const chatTabsAll = useChatTabStore((s) => s.tabs);
  const linkSession = useChatTabStore((s) => s.linkSession);
  const renameTab = useChatTabStore((s) => s.renameTab);

  const [sourceFilter, setSourceFilter] = useState<AgentSource | null>(null);
  const unifiedGroups = useUnifiedAgents(sourceFilter ? { source: sourceFilter } : undefined);
  // Unfiltered groups feed the Needs attention section so an errored or
  // waiting-review agent stays visible even when a source filter is applied.
  const allGroups = useUnifiedAgents();

  const allRows = useMemo(() => {
    return unifiedGroups.flatMap((g) => g.rows);
  }, [unifiedGroups]);

  // Selection resolves against the UNFILTERED rows so a Needs attention row
  // (fed from allGroups) can be focused even when a source filter would hide
  // it from the list. Resolving against the filtered allRows would leave
  // selectedAgent null and blank the center/right panels.
  const allRowsUnfiltered = useMemo(
    () => allGroups.flatMap((g) => g.rows),
    [allGroups],
  );

  const attentionRows = useMemo(() => collectNeedsAttention(allGroups), [allGroups]);

  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const selectedAgent = useMemo(
    () => allRowsUnfiltered.find((r) => r.id === selectedAgentId) ?? null,
    [allRowsUnfiltered, selectedAgentId],
  );

  const selectedRun = useMemo(() => {
    if (!selectedAgent || selectedAgent.source !== "workflow") return null;
    return runs.find((r) => r.id === selectedAgent.nativeId) ?? null;
  }, [selectedAgent, runs]);

  const projectMap = useMemo(
    () => new Map(worldProjects.map((p) => [Number(p.id), p])),
    [worldProjects],
  );

  const [selectedChatTab, selectedSession] = useMemo((): [ChatTab | null, AiChatSession | null] => {
    if (!selectedAgent || selectedAgent.source !== "chat") return [null, null];
    const tabs = selectedAgent.projectId != null
      ? chatTabsAll[selectedAgent.projectId] ?? []
      : Object.values(chatTabsAll).flat();
    const tab = tabs.find((t) => t.id === selectedAgent.nativeId) ?? null;
    const session = tab?.sessionId ? sessions.get(tab.sessionId) ?? null : null;
    return [tab, session];
  }, [selectedAgent, sessions, chatTabsAll]);

  const chatHasLocalPath =
    selectedAgent?.source === "chat" &&
    selectedAgent.projectId != null &&
    !!projectMap.get(selectedAgent.projectId)?.localPath;

  // A selected chat may reference a session persisted to disk but not yet loaded
  // into memory (e.g. after an app restart). Hydrate it on demand so the actual
  // conversation shows instead of a misleading "start a new chat" welcome screen.
  const [chatHydrationFailed, setChatHydrationFailed] = useState(false);
  useEffect(() => {
    const sid = selectedChatTab?.sessionId;
    if (!sid || sessions.has(sid)) {
      setChatHydrationFailed(false);
      return;
    }
    let cancelled = false;
    setChatHydrationFailed(false);
    useAiChatStore
      .getState()
      .hydrateSession(sid)
      .then((ok) => { if (!cancelled && !ok) setChatHydrationFailed(true); })
      .catch(() => { if (!cancelled) setChatHydrationFailed(true); });
    return () => { cancelled = true; };
  }, [selectedChatTab, sessions]);

  const [selectedProjectId, setSelectedProjectId] = useState<string>("all");
  const [showQuickFix, setShowQuickFix] = useState(false);
  const [showIntakeTemplates, setShowIntakeTemplates] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [leftPanelWidth, setLeftPanelWidth] = useState(DEFAULT_LEFT_WIDTH);
  const [rightPanelWidth, setRightPanelWidth] = useState(DEFAULT_RIGHT_WIDTH);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  const [isResizingRight, setIsResizingRight] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasAutoSelected = useRef(false);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    if (isResizingLeft) {
      const newWidth = e.clientX - containerRect.left;
      setLeftPanelWidth(Math.max(MIN_PANEL_WIDTH, Math.min(MAX_LEFT_PANEL_WIDTH, newWidth)));
    }
    if (isResizingRight) {
      const newWidth = containerRect.right - e.clientX;
      setRightPanelWidth(Math.max(MIN_PANEL_WIDTH, Math.min(MAX_RIGHT_PANEL_WIDTH, newWidth)));
    }
  }, [isResizingLeft, isResizingRight]);

  const handleMouseUp = useCallback(() => {
    setIsResizingLeft(false);
    setIsResizingRight(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    if (isResizingLeft || isResizingRight) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizingLeft, isResizingRight, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // Auto-select first agent
  useEffect(() => {
    if (hasAutoSelected.current || selectedAgentId) return;
    if (allRows.length === 0) return;
    const preferred =
      allRows.find((r) => isActive(r.status))
      ?? allRows.find((r) => r.status === "error")
      ?? allRows[0];
    if (preferred) {
      setSelectedAgentId(preferred.id);
      hasAutoSelected.current = true;
    }
  }, [allRows, selectedAgentId]);

  // Hydrate workflow activity when a workflow agent is selected
  useEffect(() => {
    if (!selectedAgent || selectedAgent.source !== "workflow") return;
    selectRun(selectedAgent.nativeId);
    void hydrateRunActivityFromWorkflow(selectedAgent.nativeId);
  }, [selectedAgent?.id]);

  const projectsList = useMemo(() => {
    const projectsById = new Map<number, string>();
    for (const project of worldProjects) {
      projectsById.set(Number(project.id), project.name);
    }
    return Array.from(projectsById.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [worldProjects]);

  const handleStartChat = useCallback(
    (mode: "plan" | "agent", context: { projectId?: number; projectName?: string }) => {
      createChatRun({
        mode,
        projectId: context.projectId,
        projectName: context.projectName,
      });
    },
    [createChatRun],
  );

  const handleFocusAgent = useCallback((row: UnifiedAgentRow) => {
    setSelectedAgentId(row.id);
    hasAutoSelected.current = true;
  }, []);

  const handleDeleteAgent = useCallback(async (row: UnifiedAgentRow) => {
    if (isActive(row.status)) return;
    try {
      await ACTION_HANDLERS[row.source].delete?.(row);
      setSelectedAgentId((prev) => (prev === row.id ? null : prev));
    } catch (err) {
      console.error("[AgentView] delete failed:", err);
    }
  }, []);

  // Filter rows
  const filteredRows = useMemo(() => {
    let rows = allRows;
    if (selectedProjectId !== "all") {
      const pid = Number(selectedProjectId);
      rows = rows.filter((r) => r.projectId === pid);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(
        (r) => r.title.toLowerCase().includes(q) || r.projectName.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [allRows, selectedProjectId, searchQuery]);

  // Source counts
  const sourceCounts = useMemo(() => {
    const counts = { all: allRows.length, chat: 0, terminal: 0, workflow: 0 };
    for (const r of allRows) counts[r.source]++;
    return counts;
  }, [allRows]);

  // Chat send handler
  const handleChatSend = useCallback(async (message: string, _attachments: ChatAttachment[]) => {
    if (!selectedChatTab || !selectedAgent) return;
    try {
      if (selectedChatTab.sessionId && selectedSession) {
        await sendMessage(selectedChatTab.sessionId, message);
      } else {
        const project = selectedAgent.projectId != null ? projectMap.get(selectedAgent.projectId) : undefined;
        const localPath = project?.localPath;
        if (!localPath) return;
        const sessionId = await createSession(selectedChatTab.provider, localPath, message);
        linkSession(selectedChatTab.id, sessionId);
        const name = message.length > 40 ? message.slice(0, 37) + "..." : message;
        renameTab(selectedChatTab.id, name);
      }
    } catch (err) {
      console.error("[AgentView] chat send failed:", err);
    }
  }, [selectedChatTab, selectedSession, selectedAgent, projectMap, sendMessage, createSession, linkSession, renameTab]);

  const handleChatStop = useCallback(() => {
    if (selectedChatTab?.sessionId) {
      stopSession(selectedChatTab.sessionId);
    }
  }, [selectedChatTab, stopSession]);

  if (isLoading && runs.length === 0 && allRows.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background" data-testid="container-agent-view" ref={containerRef}>
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* LEFT PANEL — Agent list */}
        {!leftCollapsed && (
        <div
          style={{ width: leftPanelWidth }}
          className="flex-shrink-0 flex flex-col bg-muted/30 dark:bg-zinc-950/80"
        >
          <div className="px-2 pt-2 pb-1.5 border-b border-border/30 space-y-1.5">
            {/* Row 1: Project filter + Search + New */}
            <div className="flex items-center gap-1.5">
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger className="w-[110px] shrink-0 h-7 text-xs bg-muted/30 border-border/50">
                  <FolderOpen className="w-3 h-3 mr-1 opacity-50" />
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projectsList.map(project => (
                    <SelectItem key={project.id} value={project.id.toString()}>{project.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/50" />
                <input
                  type="text"
                  placeholder="Search..."
                  aria-label="Search agents"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-7 pl-7 pr-2 text-xs bg-muted/30 border border-border/50 rounded-md placeholder:text-muted-foreground/40 focus:outline-none focus:ring-1 focus:ring-ring/50"
                />
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label="New agent"
                    className="shrink-0 flex items-center justify-center w-7 h-7 rounded-[6px] bg-[var(--colony-brand)] text-white hover:brightness-110 hover:-translate-y-px active:scale-[0.98] transition-all duration-150 cursor-pointer"
                    style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12), 0 1px 2px rgba(0,0,0,0.2)", transitionTimingFunction: "var(--ease-out-expo)" }}
                  >
                    <Plus size={14} strokeWidth={2} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => setShowIntakeTemplates(true)}>
                    <Sparkles className="w-4 h-4 mr-2" />
                    New Agent
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowQuickFix(true)}>
                    <Wrench className="w-4 h-4 mr-2" />
                    Quick Fix
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Row 2: Source filter pills */}
            <div className="flex items-center gap-1 h-7">
              {(["all", "chat", "terminal", "workflow"] as const).map((src) => {
                const isActive = src === "all" ? !sourceFilter : sourceFilter === src;
                const count = src === "all" ? sourceCounts.all : sourceCounts[src];
                const Icon = src === "all" ? null : SOURCE_ICONS[src];
                return (
                  <button
                    key={src}
                    onClick={() => setSourceFilter(src === "all" ? null : src)}
                    className={cn(
                      "flex items-center gap-1 h-6 px-2 rounded-md text-[10px] font-medium transition-colors cursor-pointer",
                      isActive
                        ? "bg-[var(--colony-surface)] text-colony-text-primary shadow-sm"
                        : "text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-white/[0.04]",
                    )}
                  >
                    {Icon && <Icon size={11} strokeWidth={1.5} />}
                    <span>{src === "all" ? "All" : SOURCE_LABELS[src]}</span>
                    {count > 0 && (
                      <span className="text-[9px] tabular-nums opacity-60">{count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <NeedsAttentionSection rows={attentionRows} onFocus={handleFocusAgent} />

          <ScrollArea className="flex-1">
            <div className="py-1">
              {filteredRows.map((row) => (
                <UnifiedAgentCard
                  key={row.id}
                  row={row}
                  isSelected={row.id === selectedAgentId}
                  onSelect={() => setSelectedAgentId(row.id)}
                  onDelete={() => handleDeleteAgent(row)}
                  category={row.projectId != null ? projectMap.get(row.projectId)?.project_category : undefined}
                />
              ))}
              {filteredRows.length === 0 && (
                <div className="text-center py-16 px-4">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Play className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium mb-1">No agents yet</p>
                  <p className="text-xs text-muted-foreground mb-4 max-w-[200px] mx-auto">
                    Start a chat, terminal, or workflow to see agents here
                  </p>
                  <Button size="sm" variant="default" className="text-xs" onClick={() => setShowIntakeTemplates(true)}>
                    Create a task
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
        )}

        {/* Left panel resize handle */}
        <div
          className="w-[1px] flex-shrink-0 bg-border/40 relative group"
          onMouseDown={(e) => {
            if (!leftCollapsed && e.target === e.currentTarget) setIsResizingLeft(true);
          }}
          style={{ cursor: leftCollapsed ? 'default' : 'col-resize' }}
        >
          <div className="absolute inset-y-0 -left-1 -right-1 cursor-col-resize"
               onMouseDown={() => !leftCollapsed && setIsResizingLeft(true)} />
          <button
            onClick={() => setLeftCollapsed(!leftCollapsed)}
            aria-label={leftCollapsed ? "Expand left panel" : "Collapse left panel"}
            aria-expanded={!leftCollapsed}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-muted border border-border/60 flex items-center justify-center text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted/80 transition-colors z-10"
          >
            <ChevronLeft className={cn("w-3 h-3 transition-transform", leftCollapsed && "rotate-180")} />
          </button>
        </div>

        {/* CENTER PANEL — Content */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <CenterPanel
            selectedAgent={selectedAgent}
            selectedRun={selectedRun}
            selectedSession={selectedSession}
            selectedChatTab={selectedChatTab}
            chatHydrationFailed={chatHydrationFailed}
            onChatSend={handleChatSend}
            onChatStop={handleChatStop}
            chatDisabled={!chatHasLocalPath}
          />
        </div>

        {/* Right panel resize handle */}
        <div
          className="w-[1px] flex-shrink-0 bg-border/40 relative group"
          onMouseDown={(e) => {
            if (!rightCollapsed && e.target === e.currentTarget) setIsResizingRight(true);
          }}
          style={{ cursor: rightCollapsed ? 'default' : 'col-resize' }}
        >
          <div className="absolute inset-y-0 -left-1 -right-1 cursor-col-resize"
               onMouseDown={() => !rightCollapsed && setIsResizingRight(true)} />
          <button
            onClick={() => setRightCollapsed(!rightCollapsed)}
            aria-label={rightCollapsed ? "Expand right panel" : "Collapse right panel"}
            aria-expanded={!rightCollapsed}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-muted border border-border/60 flex items-center justify-center text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted/80 transition-colors z-10"
          >
            <ChevronRight className={cn("w-3 h-3 transition-transform", rightCollapsed && "rotate-180")} />
          </button>
        </div>

        {/* RIGHT PANEL — Evidence / Details */}
        {!rightCollapsed && (
          <div
            style={{ width: rightPanelWidth }}
            className="flex-shrink-0 overflow-hidden bg-muted/30 dark:bg-zinc-950/80"
          >
            <RightPanel
              selectedAgent={selectedAgent}
              selectedRun={selectedRun}
              onClose={() => setSelectedAgentId(null)}
            />
          </div>
        )}
      </div>

      <QuickFix
        isOpen={showQuickFix}
        onClose={() => setShowQuickFix(false)}
        projects={projectsList}
        enableChatModes={isCloudWorkspace}
        onStartChat={handleStartChat}
      />

      <IntakeTemplates
        isOpen={showIntakeTemplates}
        onClose={() => setShowIntakeTemplates(false)}
        onSubmit={async (type, data, context) => {
          const intakeProjectId =
            typeof (data as { projectId?: number }).projectId === "number"
              ? (data as { projectId?: number }).projectId
              : undefined;
          if (typeof intakeProjectId !== "number") {
            setShowIntakeTemplates(false);
            return;
          }
          try {
            const title = (data as { title?: string }).title || "Untitled";
            const description = (data as { description?: string }).description || "";
            const issueType = workflowIssueTypeToTaskIssueType(context?.workflowIssueType || type);
            await addTask(
              {
                projectId: intakeProjectId,
                title,
                description,
                metadata: {
                  startInput: { title, description },
                  workflowName: context?.workflowName,
                  requiredStartFields: context?.requiredStartFields,
                },
                pipelineStage: "backlog",
                status: "pending",
                issueType,
                workflowId: context?.workflowId,
              },
              intakeProjectId,
            );
          } catch (err) {
            console.error("Failed to create task from intake:", err);
          }
          setShowIntakeTemplates(false);
        }}
        projects={projectsList}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Center Panel — routes content by agent source
// ---------------------------------------------------------------------------

function CenterPanel({
  selectedAgent,
  selectedRun,
  selectedSession,
  selectedChatTab,
  chatHydrationFailed,
  onChatSend,
  onChatStop,
  chatDisabled,
}: {
  selectedAgent: UnifiedAgentRow | null;
  selectedRun: Run | null;
  selectedSession: import("@/types/ai-chat").AiChatSession | null;
  selectedChatTab: import("@/stores/chatTabStore").ChatTab | null;
  chatHydrationFailed: boolean;
  onChatSend: (message: string, attachments: ChatAttachment[]) => void;
  onChatStop: () => void;
  chatDisabled: boolean;
}) {
  if (!selectedAgent) {
    return <AIConversationEmpty />;
  }

  if (selectedAgent.source === "workflow" && selectedRun) {
    const showRunError =
      selectedRun.status === "failed" || selectedRun.status === "blocked";
    const conversation = (
      <AIConversation
        threads={selectedRun.chatThreads || []}
        sessionId={selectedRun.activeSessionId || undefined}
        runTitle={selectedRun.title}
        isRunning={selectedRun.status === "running"}
        onSendMessage={(msg) => {
          if (selectedRun?.activeSessionId) {
            useAiChatStore.getState().sendMessage(selectedRun.activeSessionId, msg);
          }
        }}
        onPauseResume={() => {
          if (selectedRun?.activeSessionId) {
            useAiChatStore.getState().stopSession(selectedRun.activeSessionId);
          }
        }}
        runAge={(() => {
          const now = new Date();
          const then = new Date(selectedRun.startedAt);
          const diff = Math.floor((now.getTime() - then.getTime()) / 1000 / 60);
          if (diff < 60) return `${diff}m`;
          if (diff < 1440) return `${Math.floor(diff / 60)}h`;
          const weeks = Math.floor(diff / 1440 / 7);
          return weeks > 0 ? `${weeks}w` : `${Math.floor(diff / 1440)}d`;
        })()}
        filesChanged={selectedRun.filesChanged.length}
        additions={selectedRun.filesChanged.reduce((sum, f) => sum + f.additions, 0)}
        deletions={selectedRun.filesChanged.reduce((sum, f) => sum + f.deletions, 0)}
        modelsUsed={selectedRun.aiActivity?.modelsUsed?.map(m => {
          const modelLabels: Record<string, string> = {
            'claude-opus-4.6': 'Opus 4.6',
            'claude-sonnet-4.5': 'Sonnet 4.5',
            'claude-haiku-4.5': 'Haiku 4.5',
            'claude-code': 'Claude Code',
            'codex-5.3': 'Codex 5.3',
            'o3': 'o3',
            'gpt-4o': 'GPT-4o',
            'gemini-2.5-pro': 'Gemini 2.5 Pro',
            'gemini-2.5-flash': 'Gemini 2.5 Flash',
            'local': 'Local',
            'claude': 'Claude',
            'codex': 'Codex',
            'gpt-4': 'GPT-4',
            'gpt-3.5': 'GPT-3.5',
            'gemini': 'Gemini',
          };
          return modelLabels[m] || m;
        })}
      />
    );

    if (showRunError) {
      return (
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 p-3">
            <RunErrorCard
              runId={selectedRun.id}
              status={selectedRun.status === "failed" ? "failed" : "blocked"}
            />
          </div>
          <div className="min-h-0 flex-1">{conversation}</div>
        </div>
      );
    }

    return conversation;
  }

  if (selectedAgent.source === "chat") {
    const provider = selectedChatTab?.provider ?? (selectedAgent.provider as AiProvider) ?? "claude-code";
    const isStreaming = selectedSession?.state === "running";

    // Tab points at a persisted session that isn't in memory: show why the
    // conversation isn't visible instead of the misleading "start a new chat"
    // welcome screen that ChatConversation renders for an empty session.
    if (selectedChatTab?.sessionId && !selectedSession) {
      return (
        <div className="flex-1 min-h-0 flex items-center justify-center p-6 text-center">
          <div className="text-sm text-colony-text-tertiary">
            {chatHydrationFailed
              ? "This conversation's history could not be loaded — the session file may be missing."
              : "Loading conversation…"}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col flex-1 min-h-0">
        <ChatConversation
          session={selectedSession}
          provider={provider}
          isSessionRunning={isStreaming}
          onQuickAction={(prompt) => onChatSend(prompt, [])}
        />
        <ChatInput
          onSend={onChatSend}
          onStop={onChatStop}
          isStreaming={isStreaming}
          disabled={chatDisabled}
        />
      </div>
    );
  }

  if (selectedAgent.source === "terminal") {
    return <TerminalCenterPanel agent={selectedAgent} />;
  }

  return <AIConversationEmpty />;
}

function TerminalCenterPanel({ agent }: { agent: UnifiedAgentRow }) {
  // Focus the selected terminal within its scope so the embedded view shows it.
  useEffect(() => {
    if (agent.scopeKey) {
      useLocalTerminalStore.getState().setActiveTab(agent.scopeKey, agent.nativeId);
    }
  }, [agent.scopeKey, agent.nativeId]);

  // Render the live terminal inline so you can type into the running session
  // (e.g. chat with Claude Code) without leaving the agent panel.
  if (agent.scopeKey && agent.cwd) {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <Suspense
          fallback={
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Loading terminal…
            </div>
          }
        >
          <LocalTerminalView cwd={agent.cwd} scopeKey={agent.scopeKey} className="flex-1" />
        </Suspense>
      </div>
    );
  }

  return <TerminalInfoCard agent={agent} />;
}

function TerminalInfoCard({ agent }: { agent: UnifiedAgentRow }) {
  const openFolder = useCallback(async () => {
    if (!agent.cwd) return;
    try {
      const tauri = (window as any).__TAURI__;
      if (tauri?.shell?.open) await tauri.shell.open(agent.cwd);
    } catch {}
  }, [agent.cwd]);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8">
      <div className="flex flex-col items-center gap-4 max-w-[400px]">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center relative overflow-hidden">
          <span className="absolute inset-0 opacity-10" style={{ background: "var(--colony-green)" }} />
          <Terminal size={24} className="relative" style={{ color: "var(--colony-green)" }} />
        </div>
        <div className="text-center">
          <p className="text-[14px] text-colony-text-primary font-medium">{agent.title}</p>
          {agent.cwd && (
            <p className="text-[12px] text-colony-text-tertiary mt-1 font-mono truncate max-w-[360px]">
              {agent.cwd}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 mt-2">
          <StatusPill status={agent.status} />
          {agent.cwd && (
            <button
              onClick={openFolder}
              className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-colony-border-subtle bg-white/[0.02] hover:bg-colony-surface-hover text-[12px] text-colony-text-secondary hover:text-colony-text-primary transition-all cursor-pointer"
            >
              <ExternalLink size={13} />
              Open folder
            </button>
          )}
        </div>
        <p className="text-[11px] text-colony-text-tertiary text-center mt-4">
          Terminal sessions can be viewed in the project's terminal tab. Click the terminal entry in the sidebar to switch there.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right Panel — Evidence for workflows, detail for others
// ---------------------------------------------------------------------------

function RightPanel({
  selectedAgent,
  selectedRun,
  onClose,
}: {
  selectedAgent: UnifiedAgentRow | null;
  selectedRun: Run | null;
  onClose: () => void;
}) {
  if (!selectedAgent) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground/40 px-6">
        <div className="text-center">
          <Shield className="w-8 h-8 mx-auto mb-2" />
          <p className="text-xs font-medium mb-1">Details</p>
          <p className="text-[10px] leading-relaxed">
            Select an agent to see details
          </p>
        </div>
      </div>
    );
  }

  if (selectedAgent.source === "workflow" && selectedRun) {
    return (
      <FirstUseHint id="evidence-panel" hint="Review CI results, risk assessment, and make your merge decision here">
        <EvidencePanel run={selectedRun} onClose={onClose} />
      </FirstUseHint>
    );
  }

  return <AgentDetailPanel agent={selectedAgent} />;
}

function AgentDetailPanel({ agent }: { agent: UnifiedAgentRow }) {
  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-3 border-b border-border/30">
        <div className="flex items-center gap-2">
          {(() => {
            const Icon = SOURCE_ICONS[agent.source];
            return <Icon size={14} className="text-colony-text-tertiary" />;
          })()}
          <span className="text-[13px] font-medium text-colony-text-primary truncate">{agent.title}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        <DetailRow label="Source" value={SOURCE_LABELS[agent.source]} />
        <DetailRow label="Status">
          <StatusPill status={agent.status} />
        </DetailRow>
        <DetailRow label="Project" value={agent.projectName} />
        {agent.provider && (
          <DetailRow label="Provider">
            <div className="flex items-center gap-1.5">
              <img src={PROVIDER_META[agent.provider].logo} alt="" className="w-3.5 h-3.5 rounded-sm object-contain" />
              <span className="text-[12px] text-colony-text-secondary">{PROVIDER_META[agent.provider].label}</span>
            </div>
          </DetailRow>
        )}
        {agent.cwd && (
          <DetailRow label="Directory">
            <span className="text-[11px] text-colony-text-secondary font-mono truncate block max-w-[240px]">{agent.cwd}</span>
          </DetailRow>
        )}
        {agent.costUsd != null && agent.costUsd > 0 && (
          <DetailRow label="Cost" value={`$${agent.costUsd.toFixed(4)}`} />
        )}
        {agent.lastActivityAt && (
          <DetailRow label="Last Activity" value={formatTimeAgo(agent.lastActivityAt)} />
        )}
        {agent.startedAt && (
          <DetailRow label="Started" value={formatTimeAgo(agent.startedAt)} />
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-[11px] text-colony-text-tertiary whitespace-nowrap">{label}</span>
      {children ?? <span className="text-[12px] text-colony-text-secondary text-right truncate">{value}</span>}
    </div>
  );
}

function StatusPill({ status }: { status: UnifiedAgentStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded relative overflow-hidden">
      <span className="absolute inset-0 opacity-[0.08]" style={{ background: cfg.color }} />
      <span className="relative flex items-center gap-1" style={{ color: cfg.color }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.color }} />
        {cfg.label}
      </span>
    </span>
  );
}

function formatTimeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000 / 60);
  if (diff < 1) return "just now";
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
}

// ---------------------------------------------------------------------------
// Unified Agent Card — left panel item
// ---------------------------------------------------------------------------

function UnifiedAgentCard({
  row,
  isSelected,
  onSelect,
  onDelete,
  category,
}: {
  row: UnifiedAgentRow;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  category?: "coding" | "marketing";
}) {
  const statusCfg = STATUS_CONFIG[row.status];
  const SourceIcon = SOURCE_ICONS[row.source];

  const StatusIcon = statusIcon(row.status);
  const statusIconEl = (
    <StatusIcon
      className={cn("w-3.5 h-3.5", isActive(row.status) && "animate-spin")}
      style={{ color: statusCfg.color }}
    />
  );

  const timeSince = formatTimeAgo(row.lastActivityAt ?? row.startedAt ?? 0);
  const timeDisplay = (row.lastActivityAt || row.startedAt) ? timeSince : "";

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <button
          className={cn(
            "w-full text-left px-2 py-1.5 mx-0.5 rounded-[6px] transition-all duration-[120ms] cursor-pointer group relative mb-0.5 last:mb-0",
            isSelected
              ? "bg-[var(--colony-surface)] shadow-[var(--shadow-subtle)]"
              : "bg-transparent hover:bg-[rgba(var(--colony-text-primary-rgb,255,255,255),0.04)]"
          )}
          style={{ transitionTimingFunction: "var(--ease-out-expo)" }}
          onClick={onSelect}
          aria-label={row.title}
          aria-pressed={isSelected}
        >
          {isSelected && (
            <div className="absolute left-0 top-2 bottom-2 w-[2px] rounded-full bg-[var(--colony-brand)]" />
          )}

          <div className={cn("flex flex-col gap-0.5", isSelected && "pl-1.5")}>
            <div className="flex items-center gap-2">
              <div className="w-[16px] shrink-0 flex items-center justify-center">
                {statusIconEl}
              </div>
              <span className="text-[13px] truncate flex-1 leading-[1.3] tracking-[-0.01em] font-normal text-[var(--colony-text-secondary)]">
                {row.title}
              </span>
              <span className="text-[10px] text-[var(--colony-text-tertiary)] font-mono shrink-0 tabular-nums tracking-[0.02em]">
                {timeDisplay.replace(" ago", "")}
              </span>
            </div>

            <div className="flex items-center gap-1.5 pl-[24px]">
              <span className="text-[11px] font-medium" style={{ color: statusCfg.color, opacity: 0.8 }}>
                {statusCfg.label}
              </span>
              <span className="flex-1" />
              <SourceIcon size={10} strokeWidth={1.5} className="text-colony-text-tertiary opacity-60" />
              <span className="text-[10px] text-[var(--colony-text-tertiary)] truncate max-w-[80px]">
                {row.projectName}
              </span>
            </div>
          </div>
        </button>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-44">
        <ContextMenuItem
          onClick={() => ACTION_HANDLERS[row.source].focus?.(row)}
          className="text-[12px] gap-2"
        >
          <Eye className="w-3.5 h-3.5" />
          Focus
        </ContextMenuItem>
        {row.status === "running" && ACTION_HANDLERS[row.source].stop && (
          <ContextMenuItem
            onClick={() => ACTION_HANDLERS[row.source].stop?.(row)}
            className="text-[12px] gap-2"
          >
            <Pause className="w-3.5 h-3.5" />
            Stop
          </ContextMenuItem>
        )}
        {row.cwd && ACTION_HANDLERS[row.source].open_folder && (
          <ContextMenuItem
            onClick={() => ACTION_HANDLERS[row.source].open_folder?.(row)}
            className="text-[12px] gap-2"
          >
            <FolderOpen className="w-3.5 h-3.5" />
            Open Folder
          </ContextMenuItem>
        )}
        <ContextMenuSeparator />
        <ContextMenuItem
          onClick={onDelete}
          disabled={row.status === "running"}
          className="text-[12px] gap-2 text-colony-red focus:text-colony-red"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
